// Revision Settings Storage
// Manages budget revision behaviour settings (cascade toggles, target category, etc.)
//
// API-backed with module-level cache for synchronous reads.
// Falls back to localStorage/defaults if the API is unavailable.
// API endpoints: GET/PUT /api/settings/budget/revision-settings
// DB table: OrgBudgetRevisionSettings

import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';
import type { BudgetRevisionSettings } from '@/types/resource-budget-settings.types';
import { DEFAULT_REVISION_REASON_CATEGORIES } from '@/types/resource-budget-settings.types';

const STORAGE_KEY = 'budget_revision_settings';

export const DEFAULT_REVISION_SETTINGS: BudgetRevisionSettings = {
  autoUpdateCostLabour: true,
  autoUpdateSalesLabour: false,
  requireRevisionNotes: false,
  requireRevisionNotesOnCostSave: false,
  requireRevisionNotesOnSalesSave: false,
  revisionReasonCategories: DEFAULT_REVISION_REASON_CATEGORIES,
  cascadeTargetCategoryId: null,
};

// ── Module-level cache ──────────────────────────────────────────────────────
let _cache: BudgetRevisionSettings = { ...DEFAULT_REVISION_SETTINGS };
let _hasFetched = false;

/**
 * Synchronous read — returns the cached revision settings.
 * Always returns a valid object (defaults if the API hasn't been called yet).
 * Falls back to localStorage for legacy data on first access.
 */
export function getRevisionSettings(): BudgetRevisionSettings {
  if (!_hasFetched) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<BudgetRevisionSettings>;
        _cache = { ...DEFAULT_REVISION_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('Error loading revision settings from localStorage:', error);
    }
  }
  return _cache;
}

/**
 * Fetch revision settings from the server and update the cache.
 * Should be called during app bootstrap and after saving.
 */
export async function fetchRevisionSettings(): Promise<BudgetRevisionSettings> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<Partial<BudgetRevisionSettings>>(
      `${baseUrl}/settings/budget/revision-settings`
    );
    _cache = { ...DEFAULT_REVISION_SETTINGS, ..._cache, ...data };
    _hasFetched = true;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }
  } catch (error) {
    console.error('Error fetching revision settings from API:', error);
    if (!_hasFetched) {
      _cache = getRevisionSettings();
    }
  }
  return _cache;
}

/**
 * Save revision settings to the server and update the cache.
 * Falls back to localStorage-only save if the API is unavailable.
 */
export async function saveRevisionSettings(settings: BudgetRevisionSettings): Promise<void> {
  _cache = { ...DEFAULT_REVISION_SETTINGS, ...settings };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }

  const baseUrl = getApiBaseUrl();
  await apiFetch<void>(
    `${baseUrl}/settings/budget/revision-settings`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }
  );
  _hasFetched = true;
}
